// email-builder.tsx
import { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

import EmailBuilderContentSettingsTab from "containers/admin/clients/touchpoint/components/email-builder/email-builder-content-settings-tab/email-builder-content-settings-tab";
import EmailBuilderDesignTab from "containers/admin/clients/touchpoint/components/email-builder/email-builder-design-tab/email-builder-design-tab";
import EmailBuilderPreviewTab from "containers/admin/clients/touchpoint/components/email-builder/email-builder-preview-tab/email-builder-preview-tab";
import Builder from "containers/admin/clients/touchpoint/components/builder/builder";
import {
  BuilderTabNav,
  BuilderTabId,
} from "containers/admin/clients/touchpoint/components/builder/builder-tab-nav/builder-tab-nav";
import useEmailSettingsFields from "containers/admin/clients/touchpoint/components/email-builder/use-email-builder-form-fields";
import { useSaveTouchpoint } from "containers/admin/clients/touchpoint/components/builder/use-save-touchpoint";

import { Content, ContentWrapper } from "components/partials/layout/layout";
import { Form, FormProps } from "ui/forms";
import { LoadingSpinner } from "components/partials/loading-screen/loading-screen";
import { LockReleaseModal } from "components/modals/lock-release-modal/lock-release-modal";

import useEmailTypes from "containers/admin/clients/client/components/email-types/use-email-types";
import { useBuilderContent } from "hooks/api/use-touchpoint-versions";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";

import type { EmailAttributes } from "hooks/api/use-touchpoint-versions";
import { fetchBeeConfig } from "hooks/api/use-bee-config";

import { usePagePermission } from "hooks/use-page-permission";
import { addAnnotationIdToElements } from "utilities";
import { BLANK_BEE_TEMPLATE, PROGRAM_TEMPLATES_CAMPAIGN_NAME } from "utilities/constants";
import { createBeeThemeTemplate } from "utilities/bee-theme-template";

import { BeeConfig } from "types/bee";

import { Permission } from "types/auth";
import { ISenderDetails } from "containers/admin/clients/client/components/email-types/components/email-types-senders-form/email-types-senders-form.types";

import { DEFAULT_SENDER_DATA } from "containers/admin/clients/client/components/email-types/email-types.constants";

interface EmailBuilderProps {
  previewMode: boolean;
}

const EmailBuilder = ({ previewMode }: EmailBuilderProps) => {
  usePagePermission(Permission.PERM_TOUCHPOINT_READ);

  const currentClient = useCurrentClient();

  const touchpointContent = useBuilderContent() as EmailAttributes;
  const currentTouchpointVersion = touchpointContent;
  const currentTouchpoint = useCurrentTouchpoint();
  const currentCampaign = useCurrentCampaign();
  const isTemplate = currentCampaign.name === PROGRAM_TEMPLATES_CAMPAIGN_NAME;
  const emailTypeOptions = useEmailTypes();

  const { saveTouchpoint, isLockReleaseModalOpen, setIsLockReleaseModalOpen } = useSaveTouchpoint();
  const navigate = useNavigate();
  const location = useLocation();

  const [currentSender, setCurrentSender] = useState<ISenderDetails>(DEFAULT_SENDER_DATA);
  const [isNewTouchpoint, setIsNewTouchpoint] = useState(false);
  const [isSaved, setIsSaved] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [tabValidity, setTabValidity] = useState<Record<BuilderTabId, boolean>>({
    design: true,
    "content-settings": isTemplate || !!touchpointContent.publishable,
    preview: true,
  });

  useEffect(() => {
    setTabValidity((prev) => ({
      ...prev,
      "content-settings": isTemplate || !!touchpointContent.publishable,
    }));
  }, [touchpointContent.publishable, isTemplate]);

  const handleLockRelease = () => {
    setIsLockReleaseModalOpen(false);
    navigate("../../../preview", { state: location.state });
  };

  const formFields = useEmailSettingsFields({
    emailTypeOptions,
    currentTouchpointVersion,
    setCurrentSender,
    isTemplate,
  });

  useEffect(
    function detectNewTouchpoint() {
      if (!touchpointContent.data?.jsonOutput) {
        setIsNewTouchpoint(true);
      }
    },
    [touchpointContent],
  );

  useEffect(
    function triggerSetInitialValues() {
      if (isNewTouchpoint) {
        handleSetInitialValues();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isNewTouchpoint],
  );

  async function handleSetInitialValues() {
    setIsSaving(true);

    try {
      const defaultFormValues = formFields.reduce(
        (acc, field) => {
          if (field.defaultValue === undefined || field.defaultValue === null) {
            acc[field.name] = "";
          } else {
            acc[field.name] = String(field.defaultValue);
          }
          return acc;
        },
        {} as Record<string, string>,
      );

      let templateJson = BLANK_BEE_TEMPLATE;

      // Check if we should apply client theme and get bee config
      if (touchpointContent.applyClientTheme) {
        try {
          const beeConfigData = await fetchBeeConfig(currentClient.id);
          templateJson = createBeeThemeTemplate(
            beeConfigData.banner,
            beeConfigData.contentDefaults.row.styles,
          );
        } catch (error) {
          console.error("Error fetching bee config:", error);
          // Continue with default template if bee config fetch fails
        }
      }

      const newTouchpoint = {
        data: {
          htmlOutput: "<>",
          jsonOutput: addAnnotationIdToElements(templateJson),
        },
        sourceType: "BEE",
        ...defaultFormValues,
      };

      await saveTouchpoint(newTouchpoint);
      setIsSaved(true);
      setIsNewTouchpoint(false);
    } catch (error) {
      console.error("Error saving design", error); // Changed to console.error
      setIsSaved(false);
      // Add error handling to prevent infinite loading
      setIsNewTouchpoint(false); // Force exit from loading state on error
    }

    setIsSaving(false);
  }

  const lastPathSegment = location.pathname.split("/").pop();

  const currentTab = lastPathSegment as BuilderTabId;

  return (
    <Builder
      data-testid="email-builder"
      apiValue={touchpointContent}
      contentName={currentTouchpoint.name}
      previewMode={previewMode}
      permission={Permission.PERM_TOUCHPOINT_WRITE}
      isSaved={isSaved}
      setIsSaved={setIsSaved}
      isSaving={isSaving}
    >
      <BuilderTabNav
        data-testid="email-builder--tab-nav"
        isPreviewMode={previewMode}
        url={location.pathname}
        currentTab={currentTab}
        tabValidity={tabValidity}
      />

      <ContentWrapper>
        <Content sx={{ backgroundColor: "background.default", pt: 3 }}>
          {isNewTouchpoint ? (
            <div className="flex flex-1 justify-center items-center">
              <LoadingSpinner />
            </div>
          ) : (
            <Routes>
              <Route
                index
                element={
                  <Navigate
                    to={previewMode ? "preview" : "design"}
                    replace
                    state={location.state}
                  />
                }
              />

              {!previewMode && (
                <Route
                  path="design"
                  element={
                    <EmailBuilderDesignTab
                      onSave={saveTouchpoint}
                      setIsSaved={setIsSaved}
                      setIsSaving={setIsSaving}
                    />
                  }
                />
              )}

              <Route
                path="content-settings"
                element={
                  <EmailBuilderContentSettingsTab
                    previewMode={previewMode}
                    formFields={formFields}
                    emailTypeOptions={emailTypeOptions}
                    currentTouchpointVersion={currentTouchpointVersion}
                    onSave={saveTouchpoint}
                    setIsSaved={setIsSaved}
                    setIsSaving={setIsSaving}
                    setCurrentSender={setCurrentSender}
                    isTemplate={isTemplate}
                  />
                }
              />

              <Route
                path="preview"
                element={
                  <EmailBuilderPreviewTab previewMode={previewMode} currentSender={currentSender} />
                }
              />
            </Routes>
          )}
          {isLockReleaseModalOpen && <LockReleaseModal onCloseCallback={handleLockRelease} />}
        </Content>
      </ContentWrapper>
    </Builder>
  );
};

export const TouchpointBuilderTabForm = ({ children, ...rest }: FormProps) => (
  <Form width="42rem" isCentered {...rest}>
    {children}
  </Form>
);

export interface BuilderTabProps {
  previewMode?: boolean;
  themeConfig?: BeeConfig;
}

export default EmailBuilder;
